import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { VerifiedBadge } from "@/components/Badges";
import { MapPin, ArrowRight, Search } from "lucide-react";
import { CREATOR_TYPES, labelCreatorType } from "@/lib/roleCategories";

const FILTER_TYPES = [{ value: "all", label: "Tous les métiers" }, ...CREATOR_TYPES];

export default function CreatorsList() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [creators, setCreators] = useState([]);
  const [filters, setFilters] = useState({
    location: "",
    pro_type: "all",
    verified_only: false,
    q: "",
  });

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    const params = {};
    if (filters.location) params.location = filters.location;
    if (filters.pro_type && filters.pro_type !== "all") params.pro_type = filters.pro_type;
    if (filters.verified_only) params.verified_only = true;
    if (filters.q) params.q = filters.q;
    api.get("/creators", { params })
      .then(({ data }) => setCreators((Array.isArray(data) ? data : []).filter((c) => c.user_id !== user.user_id)))
      .catch(() => setCreators([]));
  }, [user, filters]);

  if (!user) return null;

  return (
    <AppShell>
      <div className="mb-8">
        <p className="caption-label text-[#C68E59]">Découvrir</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] mt-1 leading-[1.05]">
          Les <span className="italic">Créateurs</span>
        </h1>
        <p className="text-[#666] mt-3 text-sm max-w-xl">
          {user.role === "talent"
            ? "Photographes, stylistes, maquilleurs, vidéastes, marques, studios — trouvez le créateur qui fera vibrer votre prochain projet."
            : "Trouvez un photographe, un styliste, un studio ou une marque partenaire pour votre prochaine campagne — et entrez en contact directement."}
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-3xl border border-[#EADDC9] p-5 sm:p-6 mb-8" data-testid="creators-filters">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8B8680]" />
            <Input
              placeholder="Rechercher un nom, un studio…"
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              className="rounded-full pl-10 h-11"
              data-testid="creators-search"
            />
          </div>
          <Select
            value={filters.pro_type}
            onValueChange={(v) => setFilters({ ...filters, pro_type: v })}
          >
            <SelectTrigger className="rounded-full h-11" data-testid="creators-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FILTER_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            placeholder="Ville"
            value={filters.location}
            onChange={(e) => setFilters({ ...filters, location: e.target.value })}
            className="rounded-full h-11"
            data-testid="creators-location"
          />
        </div>
        <div className="flex items-center gap-2 mt-4">
          <Switch
            id="verified-only"
            checked={filters.verified_only}
            onCheckedChange={(v) => setFilters({ ...filters, verified_only: v })}
            data-testid="creators-verified-only"
          />
          <Label htmlFor="verified-only" className="text-sm text-[#1A1A1A] cursor-pointer">
            Vérifiés uniquement
          </Label>
        </div>
      </div>

      {/* Grid */}
      {creators.length === 0 ? (
        <div className="bg-white rounded-3xl border border-[#EADDC9] p-12 text-center" data-testid="creators-empty">
          <p className="font-serif text-2xl text-[#1A1A1A]">Aucun créateur trouvé</p>
          <p className="text-sm text-[#666] mt-2">Essayez d'ajuster vos filtres.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="creators-grid">
          {creators.map((c) => (
            <CreatorCard key={c.user_id} c={c} />
          ))}
        </div>
      )}
    </AppShell>
  );
}

function CreatorCard({ c }) {
  const initials = (c.company_name || c.name || "?")[0].toUpperCase();
  return (
    <Link
      to={`/createurs/${c.user_id}`}
      className="group bg-white rounded-3xl border border-[#EADDC9] p-6 hover:border-[#C68E59]/50 hover:shadow-[0_8px_24px_rgba(0,0,0,0.05)] transition-all flex flex-col h-full"
      data-testid={`creator-card-${c.user_id}`}
    >
      <div className="flex items-start gap-4">
        {c.logo_url || c.picture ? (
          <img
            src={c.logo_url || c.picture}
            alt=""
            className="w-14 h-14 rounded-2xl object-cover border border-[#EADDC9]"
          />
        ) : (
          <div className="w-14 h-14 rounded-2xl bg-[#FAF3EA] flex items-center justify-center font-serif text-2xl text-[#A26F3E] border border-[#EADDC9]">
            {initials}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-serif text-lg text-[#1A1A1A] truncate">
              {c.company_name || c.name}
            </h3>
            {c.verified && <VerifiedBadge />}
          </div>
          {c.pro_type && (
            <p className="text-xs text-[#A26F3E] mt-0.5">{labelCreatorType(c.pro_type)}</p>
          )}
        </div>
      </div>

      {c.description && (
        <p className="text-sm text-[#666] mt-4 line-clamp-3 flex-1">
          {c.description}
        </p>
      )}

      <div className="flex items-center justify-between mt-5 pt-4 border-t border-[#FAF3EA]">
        {c.location && (
          <span className="text-xs text-[#8B8680] inline-flex items-center gap-1">
            <MapPin className="w-3 h-3" /> {c.location}
          </span>
        )}
        <span className="text-xs font-semibold text-[#1A1A1A] inline-flex items-center gap-1 group-hover:text-[#C68E59] transition-colors ml-auto">
          Voir <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
        </span>
      </div>
    </Link>
  );
}
